import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
  ActivityIndicator,
  Modal,
} from 'react-native';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { WebView } from 'react-native-webview';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { BlockDefinition, PlaceholderGroup } from './types';
import { withAlpha } from '../../theme';

/** Reusable free-form drag-and-drop HTML canvas — NOT quotation-specific.
 * Operates directly on real HTML/CSS: `html` in and out of this component
 * is always a complete, standalone HTML document (the same string a
 * Handlebars/Puppeteer pipeline renders). There is no intermediate JSON/
 * tree layout model — the WebView's live DOM (read via innerHTML on every
 * mutation) IS the serialization format, tagged only with `data-block-id`/
 * `data-block-type` attributes so a document can be re-opened for editing
 * later and have each top-level element recognized as a movable block. */

export interface CanvasBuilderProps {
  html: string;
  onChange: (html: string) => void;
  blocks: BlockDefinition[];
  placeholders?: PlaceholderGroup[];
  pageWidth?: number;
  pageHeight?: number;
  pageBackground?: string;
}

// A4 at 96dpi — matches the physical PDF page size 1:1 (see
// quotation-pdf.service.ts's printToPdf, which now sizes the PDF page to
// whatever width/min-height a template's <body> declares), so a document
// built at these defaults fills the printed sheet exactly instead of
// sitting inside a mismatched hardcoded page size.
const DEFAULT_PAGE_WIDTH = 794;
const DEFAULT_PAGE_HEIGHT = 1123;

function extractBodyInner(fullHtml: string): string {
  if (!fullHtml || !fullHtml.trim()) return '';
  const match = fullHtml.match(/<body[^>]*>([\s\S]*)<\/body>/i);
  return match ? match[1] : fullHtml;
}

// The canvas only ever edits the BODY's content live — but a business's
// template may define <style>/:root (fonts, CSS variables like --primary)
// in <head>, which must survive every load/edit/save round-trip rather
// than being silently dropped in favor of the canvas's own minimal head.
function extractHeadInner(fullHtml: string): string {
  const match = fullHtml.match(/<head[^>]*>([\s\S]*?)<\/head>/i);
  return match ? match[1] : '';
}

function wrapAsDocument(inner: string, pageWidth: number, pageHeight: number, background: string, headContent: string): string {
  return `<html>
<head>
<meta charset="utf-8" />
<style>* { box-sizing: border-box; } body { margin: 0; }</style>
${headContent || ''}
</head>
<body style="position:relative;width:${pageWidth}px;min-height:${pageHeight}px;background:${background};font-family:'Tajawal', Helvetica, Arial, sans-serif;">
${inner}
</body>
</html>`;
}

const buildCanvasHTML = (pageWidth: number, pageHeight: number, background: string) => `
<!DOCTYPE html>
<html>
<head>
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
  <style>
    * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
    html, body { margin: 0; padding: 0; background: #e5e7eb; font-family: -apple-system, sans-serif; touch-action: none; }
    #page {
      position: relative;
      width: ${pageWidth}px;
      min-height: ${pageHeight}px;
      background: ${background};
      margin: 16px;
      box-shadow: 0 2px 12px rgba(0,0,0,0.15);
      overflow: hidden;
    }
    .canvas-block { cursor: grab; }
    .canvas-block.selected { outline: 2px dashed #1a56db; outline-offset: 1px; }
    .canvas-block[contenteditable="true"] { cursor: text; outline: 2px solid #1a56db; }
    .resize-handle {
      position: absolute; right: -7px; bottom: -7px; width: 16px; height: 16px;
      background: #1a56db; border: 2px solid #fff; border-radius: 50%;
      cursor: nwse-resize; box-shadow: 0 1px 3px rgba(0,0,0,0.3);
    }
  </style>
</head>
<body>
  <div id="page"></div>
  <script>
    const page = document.getElementById('page');
    let selectedEl = null;
    let dragState = null;
    let resizeState = null;
    let addCounter = 0;

    function send(obj) {
      const msg = JSON.stringify(obj);
      if (window.ReactNativeWebView) window.ReactNativeWebView.postMessage(msg);
      else if (window.parent) window.parent.postMessage(msg, '*');
    }

    // A raw {{#each items}}/{{/each}} (or #if/#unless) sitting directly
    // inside a <table>/<thead>/<tbody>/<tr> is not valid table content — the
    // instant this HTML is parsed (innerHTML=), the browser "foster-parents"
    // that bare text out from between the table tags, orphaning whatever it
    // wrapped (e.g. a row meant to repeat per item ends up static, once,
    // outside the loop). Wrapping the marker in an HTML comment sidesteps
    // this: comments are valid anywhere (never foster-parented), survive
    // DOM round-trips untouched, and Handlebars — which only scans for
    // {{ }} tokens and doesn't care about surrounding HTML — still compiles
    // them correctly (the leftover comment delimiters pair up into harmless
    // empty "<!---->" comments interleaved between real rendered rows).
    function protectHandlebarsBlocks(html) {
      return html.replace(/\\{\\{(#each\\b[^}]*|\\/each|#if\\b[^}]*|\\/if|#unless\\b[^}]*|\\/unless|else)\\}\\}/g, function (match, _g, offset, str) {
        const before = str.slice(Math.max(0, offset - 4), offset);
        const after = str.slice(offset + match.length, offset + match.length + 3);
        if (before === '<!--' && after === '-->') return match;
        return '<!--' + match + '-->';
      });
    }

    function serialize() {
      const clone = page.cloneNode(true);
      clone.querySelectorAll('.resize-handle').forEach(el => el.remove());
      clone.querySelectorAll('[data-block-id]').forEach(el => {
        el.removeAttribute('contenteditable');
        el.classList.remove('canvas-block', 'selected');
        if (el.getAttribute('class') === '') el.removeAttribute('class');
      });
      return clone.innerHTML;
    }

    function emitChange() { send({ type: 'htmlChanged', html: serialize() }); }

    function addResizeHandle(el) {
      const handle = document.createElement('div');
      handle.className = 'resize-handle';
      handle.addEventListener('pointerdown', (e) => {
        e.stopPropagation();
        resizeState = { id: el.getAttribute('data-block-id'), startX: e.clientX, startY: e.clientY, origW: el.offsetWidth, origH: el.offsetHeight };
        try { handle.setPointerCapture(e.pointerId); } catch (err) {}
      });
      handle.addEventListener('pointermove', (e) => {
        if (!resizeState || resizeState.id !== el.getAttribute('data-block-id')) return;
        const dx = e.clientX - resizeState.startX;
        const dy = e.clientY - resizeState.startY;
        el.style.width = Math.max(24, resizeState.origW + dx) + 'px';
        el.style.height = Math.max(24, resizeState.origH + dy) + 'px';
      });
      handle.addEventListener('pointerup', () => { if (resizeState) { resizeState = null; emitChange(); } });
      el.appendChild(handle);
    }

    function removeResizeHandle(el) {
      const h = el.querySelector('.resize-handle');
      if (h) h.remove();
    }

    function selectBlock(el) {
      if (selectedEl === el) return;
      if (selectedEl) { selectedEl.classList.remove('selected'); removeResizeHandle(selectedEl); }
      selectedEl = el;
      el.classList.add('selected');
      addResizeHandle(el);
      send({ type: 'selectionChanged', blockId: el.getAttribute('data-block-id'), blockType: el.getAttribute('data-block-type'), editable: el.getAttribute('data-editable') === 'true' });
    }

    function deselect() {
      if (!selectedEl) return;
      selectedEl.classList.remove('selected');
      removeResizeHandle(selectedEl);
      selectedEl = null;
      send({ type: 'selectionChanged', blockId: null });
    }

    function attachBlockHandlers(el) {
      el.classList.add('canvas-block');
      el.addEventListener('pointerdown', (e) => {
        if (el.getAttribute('contenteditable') === 'true') { e.stopPropagation(); return; }
        e.stopPropagation();
        selectBlock(el);
        dragState = { id: el.getAttribute('data-block-id'), startX: e.clientX, startY: e.clientY, origLeft: parseFloat(el.style.left) || 0, origTop: parseFloat(el.style.top) || 0 };
        try { el.setPointerCapture(e.pointerId); } catch (err) {}
      });
      el.addEventListener('pointermove', (e) => {
        if (!dragState || dragState.id !== el.getAttribute('data-block-id')) return;
        const dx = e.clientX - dragState.startX;
        const dy = e.clientY - dragState.startY;
        el.style.left = Math.max(0, dragState.origLeft + dx) + 'px';
        el.style.top = Math.max(0, dragState.origTop + dy) + 'px';
      });
      el.addEventListener('pointerup', () => { if (dragState && dragState.id === el.getAttribute('data-block-id')) { dragState = null; emitChange(); } });
      el.addEventListener('dblclick', (e) => {
        if (el.getAttribute('data-editable') === 'true') {
          e.stopPropagation();
          el.setAttribute('contenteditable', 'true');
          el.focus();
        }
      });
      el.addEventListener('blur', () => {
        if (el.getAttribute('contenteditable') === 'true') {
          el.removeAttribute('contenteditable');
          emitChange();
        }
      });
    }

    page.addEventListener('pointerdown', (e) => { if (e.target === page) deselect(); });

    function addBlock(payload) {
      addCounter += 1;
      const offset = (addCounter % 6) * 18;
      const id = 'b' + Date.now() + Math.floor(Math.random() * 1000);
      const wrap = document.createElement('div');
      wrap.setAttribute('data-block-id', id);
      wrap.setAttribute('data-block-type', payload.blockType);
      if (payload.editableText) wrap.setAttribute('data-editable', 'true');
      wrap.style.position = 'absolute';
      wrap.style.left = (24 + offset) + 'px';
      wrap.style.top = (24 + offset) + 'px';
      wrap.style.width = payload.width + 'px';
      wrap.style.height = payload.height + 'px';
      wrap.innerHTML = protectHandlebarsBlocks(payload.html);
      page.appendChild(wrap);
      attachBlockHandlers(wrap);
      selectBlock(wrap);
      emitChange();
    }

    function deleteSelected() {
      if (!selectedEl) return;
      selectedEl.remove();
      selectedEl = null;
      emitChange();
      send({ type: 'selectionChanged', blockId: null });
    }

    function bringToFront() { if (selectedEl) { page.appendChild(selectedEl); emitChange(); } }
    function sendToBack() { if (selectedEl) { page.insertBefore(selectedEl, page.firstChild); emitChange(); } }

    function insertPlaceholder(value) {
      if (!selectedEl || selectedEl.getAttribute('contenteditable') !== 'true') return;
      selectedEl.focus();
      try { document.execCommand('insertText', false, value); } catch (e) {
        selectedEl.textContent = (selectedEl.textContent || '') + value;
      }
      emitChange();
    }

    function adoptAsBlock(el) {
      // Hand-written/externally-edited HTML won't have our data-block-id/
      // data-block-type markers, and may position itself via right/bottom
      // anchors instead of left/top+width/height. Adopt it as a normal
      // block anyway (assign an id, normalize to an explicit absolute box
      // matching its current rendered position) so nothing pasted into the
      // template is inert in the canvas.
      const id = 'b' + Date.now() + Math.floor(Math.random() * 1000);
      el.setAttribute('data-block-id', id);
      if (!el.hasAttribute('data-block-type')) el.setAttribute('data-block-type', 'custom');
      const pageRect = page.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      el.style.position = 'absolute';
      el.style.left = (elRect.left - pageRect.left) + 'px';
      el.style.top = (elRect.top - pageRect.top) + 'px';
      el.style.width = elRect.width + 'px';
      el.style.height = elRect.height + 'px';
      el.style.right = '';
      el.style.bottom = '';
      attachBlockHandlers(el);
    }

    function loadHtml(inner) {
      page.innerHTML = protectHandlebarsBlocks(inner || '');
      let adopted = false;
      Array.from(page.children).forEach((el) => {
        if (el.hasAttribute('data-block-id')) {
          attachBlockHandlers(el);
        } else {
          adoptAsBlock(el);
          adopted = true;
        }
      });
      selectedEl = null;
      // Persist newly-assigned block ids/normalized positions right away so
      // a save without any further edit still keeps them (re-adopting the
      // same elements every time it's reopened is harmless but wasteful).
      if (adopted) emitChange();
      send({ type: 'loaded' });
    }

    function handleMessage(raw) {
      let data;
      try { data = JSON.parse(raw); } catch (e) { return; }
      if (data.type === 'load') loadHtml(data.html);
      else if (data.type === 'addBlock') addBlock(data);
      else if (data.type === 'deleteSelected') deleteSelected();
      else if (data.type === 'bringToFront') bringToFront();
      else if (data.type === 'sendToBack') sendToBack();
      else if (data.type === 'insertPlaceholder') insertPlaceholder(data.value);
    }

    window.addEventListener('message', (e) => handleMessage(e.data));
    if (window.ReactNativeWebView) document.addEventListener('message', (e) => handleMessage(e.data));

    send({ type: 'canvasReady' });
  </script>
</body>
</html>
`;

export const CanvasBuilder: React.FC<CanvasBuilderProps> = ({
  html,
  onChange,
  blocks,
  placeholders,
  pageWidth = DEFAULT_PAGE_WIDTH,
  pageHeight = DEFAULT_PAGE_HEIGHT,
  pageBackground = '#ffffff',
}) => {
  const styles = useThemedStyles(getStyles);
  const webViewRef = useRef<WebView>(null);
  const readyRef = useRef(false);
  const lastEmittedRef = useRef<string | null>(null);
  const headContentRef = useRef<string>('');
  const [selection, setSelection] = useState<{ id: string; type: string; editable: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  const [placeholderMenuOpen, setPlaceholderMenuOpen] = useState(false);

  const canvasHTML = buildCanvasHTML(pageWidth, pageHeight, pageBackground);
  const contentWidth = pageWidth + 32;
  const contentHeight = pageHeight + 32;

  // The canvas viewport's real pixel size is measured via onLayout rather
  // than trusted to flex propagation — a nested ScrollView doesn't hand its
  // viewport size down to children via flex (a ScrollView always sizes its
  // content to the children's natural size, that's what makes it scroll),
  // so a plain flex:1 on an inner ScrollView silently collapses to near-zero
  // height instead of filling the available screen. Measuring the outer
  // plain View (which Yoga DOES correctly flex-size) and applying that as
  // an explicit pixel height to the ScrollViews below sidesteps that.
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(1);
  const autoFitDoneRef = useRef(false);

  // A full-size page (A4 by default, 794px wide) is wider than most phone
  // screens — auto-fit the width the first time the real viewport is known
  // so editing starts at a usable zoom instead of forcing constant
  // horizontal scrolling. Only runs once; manual zoom afterward sticks.
  useEffect(() => {
    if (autoFitDoneRef.current || viewportSize.width <= 0) return;
    autoFitDoneRef.current = true;
    if (contentWidth > viewportSize.width) {
      setZoom(Math.max(0.25, viewportSize.width / contentWidth));
    }
  }, [viewportSize.width, contentWidth]);

  const zoomToFit = () => {
    if (viewportSize.width > 0) setZoom(Math.max(0.25, Math.min(1, viewportSize.width / contentWidth)));
  };

  const postToCanvas = useCallback((obj: object) => {
    const msg = JSON.stringify(obj);
    if (Platform.OS === 'web') {
      const iframe = document.getElementById('canvas-builder-iframe') as HTMLIFrameElement | null;
      if (iframe?.contentWindow) iframe.contentWindow.postMessage(msg, '*');
    } else {
      webViewRef.current?.postMessage(msg);
    }
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const handler = (event: MessageEvent) => {
      const iframe = document.getElementById('canvas-builder-iframe') as HTMLIFrameElement | null;
      if (iframe && event.source === iframe.contentWindow) handleCanvasMessage({ data: event.data });
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const pushLoad = useCallback((fullHtml: string) => {
    headContentRef.current = extractHeadInner(fullHtml);
    postToCanvas({ type: 'load', html: extractBodyInner(fullHtml) });
  }, [postToCanvas]);

  useEffect(() => {
    if (!readyRef.current) return;
    if (html === lastEmittedRef.current) return;
    pushLoad(html);
  }, [html, pushLoad]);

  const handleCanvasMessage = (event: any) => {
    try {
      const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      if (data.type === 'canvasReady') {
        readyRef.current = true;
        setLoading(false);
        pushLoad(html);
      } else if (data.type === 'htmlChanged') {
        const fullDoc = wrapAsDocument(data.html, pageWidth, pageHeight, pageBackground, headContentRef.current);
        lastEmittedRef.current = fullDoc;
        onChange(fullDoc);
      } else if (data.type === 'selectionChanged') {
        setSelection(data.blockId ? { id: data.blockId, type: data.blockType, editable: !!data.editable } : null);
      }
    } catch (_) {}
  };

  const handleAddBlock = (block: BlockDefinition) => {
    postToCanvas({
      type: 'addBlock',
      blockType: block.type,
      html: block.html,
      width: block.defaultWidth,
      height: block.defaultHeight,
      editableText: !!block.editableText,
    });
  };

  return (
    <View style={styles.container}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.palette} contentContainerStyle={styles.paletteContent}>
        {blocks.map((block) => (
          <TouchableOpacity key={block.type} style={styles.paletteItem} onPress={() => handleAddBlock(block)}>
            <Ionicons name={block.icon as any} size={18} color={colors.primary} />
            <Text style={styles.paletteItemLabel}>{block.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {selection && (
        <View style={styles.selectionBar}>
          <Text style={styles.selectionLabel} numberOfLines={1}>{selection.type}</Text>
          <View style={styles.selectionActions}>
            {selection.editable && !!placeholders?.length && (
              <TouchableOpacity style={styles.selectionBtn} onPress={() => setPlaceholderMenuOpen(true)}>
                <Ionicons name="code-slash-outline" size={16} color={colors.primary} />
              </TouchableOpacity>
            )}
            <TouchableOpacity style={styles.selectionBtn} onPress={() => postToCanvas({ type: 'sendToBack' })}>
              <Ionicons name="arrow-down-outline" size={16} color={colors.text.secondary} />
            </TouchableOpacity>
            <TouchableOpacity style={styles.selectionBtn} onPress={() => postToCanvas({ type: 'bringToFront' })}>
              <Ionicons name="arrow-up-outline" size={16} color={colors.text.secondary} />
            </TouchableOpacity>
            <TouchableOpacity style={[styles.selectionBtn, styles.deleteBtn]} onPress={() => postToCanvas({ type: 'deleteSelected' })}>
              <Ionicons name="trash-outline" size={16} color={colors.error} />
            </TouchableOpacity>
          </View>
        </View>
      )}

      <View
        style={styles.canvasViewport}
        onLayout={(e) => setViewportSize({ width: e.nativeEvent.layout.width, height: e.nativeEvent.layout.height })}
      >
        {viewportSize.height > 0 && (
          <ScrollView
            style={{ width: viewportSize.width, height: viewportSize.height }}
            contentContainerStyle={[styles.canvasScrollContent, { minWidth: viewportSize.width }]}
          >
            <ScrollView
              horizontal
              style={{ width: viewportSize.width }}
              contentContainerStyle={[styles.canvasScrollContentH, { minWidth: viewportSize.width }]}
            >
              <View style={{ width: contentWidth * zoom, height: contentHeight * zoom }}>
                <View
                  style={[
                    styles.canvasWrap,
                    { width: contentWidth, height: contentHeight, transform: [{ scale: zoom }], transformOrigin: '0 0' } as any,
                  ]}
                >
                  {Platform.OS === 'web' ? (
                    <iframe id="canvas-builder-iframe" srcDoc={canvasHTML} style={{ width: contentWidth, height: contentHeight, border: 'none' } as any} />
                  ) : (
                    <WebView
                      ref={webViewRef}
                      source={{ html: canvasHTML }}
                      style={{ width: contentWidth, height: contentHeight, backgroundColor: 'transparent' }}
                      onMessage={handleCanvasMessage}
                      javaScriptEnabled
                      domStorageEnabled
                      scrollEnabled={false}
                    />
                  )}
                  {loading && (
                    <View style={styles.canvasLoading}>
                      <ActivityIndicator size="large" color={colors.primary} />
                    </View>
                  )}
                </View>
              </View>
            </ScrollView>
          </ScrollView>
        )}

        <View style={styles.zoomControls}>
          <TouchableOpacity style={styles.zoomBtn} onPress={() => setZoom((z) => Math.max(0.25, +(z - 0.1).toFixed(2)))}>
            <Ionicons name="remove" size={16} color={colors.text.secondary} />
          </TouchableOpacity>
          <TouchableOpacity onPress={zoomToFit}>
            <Text style={styles.zoomText}>{Math.round(zoom * 100)}%</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.zoomBtn} onPress={() => setZoom((z) => Math.min(2, +(z + 0.1).toFixed(2)))}>
            <Ionicons name="add" size={16} color={colors.text.secondary} />
          </TouchableOpacity>
        </View>
      </View>

      <Modal visible={placeholderMenuOpen} transparent animationType="fade" onRequestClose={() => setPlaceholderMenuOpen(false)}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setPlaceholderMenuOpen(false)}>
          <View style={styles.placeholderMenu}>
            <ScrollView>
              {(placeholders || []).map((group) => (
                <View key={group.label}>
                  <Text style={styles.placeholderGroupLabel}>{group.label}</Text>
                  {group.items.map((item) => (
                    <TouchableOpacity
                      key={item.value}
                      style={styles.placeholderItem}
                      onPress={() => {
                        postToCanvas({ type: 'insertPlaceholder', value: item.value });
                        setPlaceholderMenuOpen(false);
                      }}
                    >
                      <Text style={styles.placeholderItemLabel}>{item.label}</Text>
                      <Text style={styles.placeholderItemValue}>{item.value}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1 },
  palette: { flexGrow: 0, borderBottomWidth: 1, borderBottomColor: colors.border, backgroundColor: colors.background.base },
  paletteContent: { paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, gap: spacing.xs },
  paletteItem: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: designTokens.borderRadius.md, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface, marginRight: spacing.xs },
  paletteItemLabel: { fontSize: typography.fontSize.xs, color: colors.textDark },
  selectionBar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: spacing.sm, paddingVertical: 6, backgroundColor: withAlpha(colors.primary, 0.06), borderBottomWidth: 1, borderBottomColor: colors.border },
  selectionLabel: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.medium, flex: 1 },
  selectionActions: { flexDirection: 'row', gap: 6 },
  selectionBtn: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background.surface, borderWidth: 1, borderColor: colors.border },
  deleteBtn: { borderColor: withAlpha(colors.error, 0.33) },
  canvasViewport: { flex: 1, backgroundColor: '#d1d5db' },
  canvasScrollContent: { flexGrow: 1, alignItems: 'center', paddingVertical: spacing.md },
  canvasScrollContentH: { flexGrow: 1, justifyContent: 'center' },
  canvasWrap: {},
  canvasLoading: { ...StyleSheet.absoluteFillObject, alignItems: 'center', justifyContent: 'center' },
  zoomControls: {
    position: 'absolute', bottom: spacing.md, alignSelf: 'center',
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.lg,
    paddingHorizontal: spacing.sm, paddingVertical: 6,
    borderWidth: 1, borderColor: colors.border,
  },
  zoomBtn: { width: 24, height: 24, alignItems: 'center', justifyContent: 'center' },
  zoomText: { fontSize: typography.fontSize.xs, color: colors.textDark, fontFamily: typography.fontFamily.medium, minWidth: 36, textAlign: 'center' },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.35)', alignItems: 'center', justifyContent: 'center', padding: spacing.lg },
  placeholderMenu: { width: '100%', maxWidth: 360, maxHeight: '70%', backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.lg, padding: spacing.md },
  placeholderGroupLabel: { fontSize: typography.fontSize.xs, color: colors.text.secondary, fontFamily: typography.fontFamily.semibold, marginTop: spacing.sm, marginBottom: 4, textTransform: 'uppercase' },
  placeholderItem: { paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: colors.border },
  placeholderItemLabel: { fontSize: typography.fontSize.sm, color: colors.textDark },
  placeholderItemValue: { fontSize: typography.fontSize.xs, color: colors.text.secondary, marginTop: 2 },
});
